import axios from 'axios';
import { auth } from '../firebase';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({ baseURL: BASE_URL });

// Attach Firebase ID token to every request
api.interceptors.request.use(async (config) => {
  const user = auth.currentUser;
  if (user) {
    const token = await user.getIdToken();
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ─── Categories ──────────────────────────────────────────
export const categoryApi = {
  getAll:              ()           => api.get('/categories'),
  getById:             (id)         => api.get(`/categories/${id}`),
  create:              (data)       => api.post('/categories', data),
  update:              (id, data)   => api.put(`/categories/${id}`, data),
  delete:              (id)         => api.delete(`/categories/${id}`),

  // Subcategories
  getAllSubs:           ()           => api.get('/categories/subcategories/all'),
  getSubsByCategory:   (catId)      => api.get(`/categories/${catId}/subcategories`),
  createSub:           (catId, d)   => api.post(`/categories/${catId}/subcategories`, d),
  updateSub:           (id, data)   => api.put(`/categories/subcategories/${id}`, data),
  deleteSub:           (id)         => api.delete(`/categories/subcategories/${id}`),
};

// ─── Products ────────────────────────────────────────────
export const productApi = {
  getAll:    (params)     => api.get('/products', { params }),
  getById:   (id)         => api.get(`/products/${id}`),
  create:    (data)       => api.post('/products', data),
  update:    (id, data)   => api.put(`/products/${id}`, data),
  delete:    (id)         => api.delete(`/products/${id}`),
};

// ─── Orders ──────────────────────────────────────────────
export const orderApi = {
  getAll:       ()              => api.get('/orders'),
  getById:      (id)            => api.get(`/orders/${id}`),
  create:       (data)          => api.post('/orders', data),
  updateStatus: (id, data)      => api.put(`/orders/${id}/status`, data),
  delete:       (id)            => api.delete(`/orders/${id}`),
};

// ─── Users ───────────────────────────────────────────────
export const userApi = {
  getAll:    ()           => api.get('/users'),
  getMe:     ()           => api.get('/users/me'),
  updateMe:  (data)       => api.put('/users/me', data),
  getById:   (id)         => api.get(`/users/${id}`),
  setRole:   (id, role)   => api.put(`/users/${id}/role`, { role }),
  delete:    (id)         => api.delete(`/users/${id}`),
};

// ─── Inquiries ───────────────────────────────────────────
export const inquiryApi = {
  getAll:       ()          => api.get('/inquiries'),
  create:       (data)      => api.post('/inquiries', data),
  updateStatus: (id, status)=> api.put(`/inquiries/${id}/status`, { status }),
  delete:       (id)        => api.delete(`/inquiries/${id}`),
};

// ─── Upload ──────────────────────────────────────────────
export const uploadApi = {
  upload: (file, folder = 'products') => {
    const form = new FormData();
    form.append('image', file);
    form.append('folder', folder);
    return api.post('/upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  delete: (filename) => api.delete('/upload', { data: { filename } }),
};

// ─── Auth ────────────────────────────────────────────────
export const authApi = {
  syncUser:  (data) => api.post('/auth/sync-user', data),
  setAdmin:  (uid)  => api.post('/auth/set-admin', { uid }),
  verify:    ()     => api.get('/auth/verify'),
};

// ─── Payment ─────────────────────────────────────────────
export const paymentApi = {
  createOrder: (orderId)         => api.post('/payment/create-order', { orderId }),
  verify:      (data)            => api.post('/payment/verify', data),
  refund:      (orderId, amount) => api.post('/payment/refund', { orderId, amount }),
};

// ─── Banners ─────────────────────────────────────────────
export const bannerApi = {
  getAll:  ()         => api.get('/banners'),
  getById: (id)       => api.get(`/banners/${id}`),
  create:  (data)     => api.post('/banners', data),
  update:  (id, data) => api.put(`/banners/${id}`, data),
  delete:  (id)       => api.delete(`/banners/${id}`),
};

// ─── Brands ──────────────────────────────────────────────
export const brandApi = {
  getAll:  ()         => api.get('/brands'),
  getById: (id)       => api.get(`/brands/${id}`),
  create:  (data)     => api.post('/brands', data),
  update:  (id, data) => api.put(`/brands/${id}`, data),
  delete:  (id)       => api.delete(`/brands/${id}`),
};

// ─── Settings (About / Contact / Brands page copy) ────────
export const settingsApi = {
  get: (key)         => api.get(`/settings/${key}`),
  save: (key, data)  => api.put(`/settings/${key}`, data),
};

export default api;
